import { withQuery } from "ufo";
import {
  Cart,
  CartCheckoutInput,
  CartItemInput,
  CartItemUpdateOptions,
} from "../models/Cart";
import { Checkout, CheckoutCreateInput } from "../models/Checkout";
import { Client } from "../types/internal";
import { resolveCurrency } from "../utils/currency";
import { error } from "../utils/error";
import { createCart } from "./cart";
import { createCheckout } from "./checkout";
import { omit } from "../utils/data";
import { getRouteQuery } from "../utils/route";

type Session = {
  user?: string;
  checkout: {
    token: string;
    data?: Checkout;
  };
  cart: {
    id: string;
    hydrated: boolean;
    data: Cart;
  };
};

const createSessionCart = (client: Client, session: Session) => {
  const api = createCart(client);

  const apply = (data?: Cart) => {
    // only apply if cart exists
    if (data?.id) {
      session.cart.hydrated = true;
      session.cart.id = data.id;
      session.cart.data.id = data.id;
      session.cart.data.subtotal = data.subtotal;
      session.cart.data.total = data.total;
      session.cart.data.items = data.items;
      session.cart.data.conditions = data.conditions;
      session.cart.data.currency = data.currency;
    }
  };

  const clearData = () => {
    session.cart.data.subtotal = 0;
    session.cart.data.total = 0;
    session.cart.data.items = [];
    session.cart.data.conditions = [];
  };

  return {
    load: async (upsert?: boolean) => {
      const result = await api.get(session.cart.id, upsert);
      if (result) {
        apply(result);
        session.cart.hydrated = true;
        return session.cart.data;
      }
    },
    add: async (item: CartItemInput | string) => {
      const result = await api.add(session.cart.id, item);
      if (result?.cart) {
        apply(result.cart);
      }
      return result?.item;
    },
    update: async (item: string, data: CartItemUpdateOptions) => {
      const result = await api.update(session.cart.id, item, data);
      apply(result?.cart);
      return result?.item;
    },
    remove: async (item: string) => {
      const result = await api.remove(session.cart.id, item);
      apply(result);
      return session.cart.data;
    },
    clear: async () => {
      await api.clear(session.cart.id);
      clearData();
      return session.cart.data;
    },
    isInCart: (gid: string) => {
      const gids = session.cart.data.items.map((item) => item.gid);
      return gids.includes(gid);
    },
    checkout: async (data?: CartCheckoutInput) => {
      return api.checkout(session.cart.id, data);
    },
    isHydrated: () => session.cart.hydrated,
  };
};

const createSessionCheckout = (client: Client, session: Session) => {
  const api = createCheckout(client);

  const apply = (data?: Checkout) => {
    // only apply if checkout exists
    if (data?.token) {
      session.checkout.token = data.token;
    }
  };

  const wrapFn = <A extends any[], R>(fn: (token: string, ...args: A) => R) => {
    return (...args: A): R => {
      if (!session.checkout.token) {
        error("No checkout instance found");
      }
      return fn(session.checkout.token, ...args);
    };
  };

  return {
    create: async (data: CheckoutCreateInput = {}) => {
      const result = await api.create(data);
      apply(result);
      return session.checkout.data;
    },
    load: async (token: string) => {
      const result = await api.get(token);
      apply(result);
      return session.checkout.data;
    },
    update: wrapFn(api.update),
    setContact: wrapFn(api.setContact),
    setShippingAddress: wrapFn(api.setShippingAddress),
    setBillingAddress: wrapFn(api.setBillingAddress),
    useShippingAddressAsBilling: wrapFn(api.useShippingAddressAsBilling),
    updateItemQuantity: wrapFn(api.updateItemQuantity),
    applyPromocode: wrapFn(api.applyPromocode),
    removePromocode: wrapFn(api.removePromocode),
    selectShipping: wrapFn(api.selectShipping),
    complete: wrapFn(api.complete),
    getShippingMethods: wrapFn(api.getShippingMethods),
    getWebUrl: () => {
      if (session.checkout.data?.web_url) {
        const routeQuery = getRouteQuery();
        return withQuery(
          session.checkout.data.web_url,
          omit(routeQuery, ["token"])
        );
      }
    },
  };
};

export const createSession = (client: Client) => {
  const session: Session = {
    checkout: {
      token: "",
    },
    cart: {
      hydrated: false,
      id: "",
      data: {
        subtotal: 0,
        total: 0,
        items: [],
        currency: resolveCurrency(client.currency),
      },
    },
  };

  return {
    cart: createSessionCart(client, session),
    checkout: createSessionCheckout(client, session),
  };
};
